import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { post } from 'api';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';

// ✅ Validation Schema
const schema = yup.object({
  date: yup.date().required('Date is required'),
  requestedLogin: yup.date().nullable(),
  requestedLogout: yup.date().nullable(),
  reason: yup.string().required('Reason is required'),
});

const defaultValues = {
  date: null,
  requestedLogin: null,
  requestedLogout: null,
  reason: '',
};

const AttendanceRegularizationDialog = ({ open, onClose, initialData = null }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  // 🔁 Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset(
        initialData || {
          ...defaultValues,
        },
      );
    }
  }, [open, initialData, reset]);

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      date: dayjs(data.date).toISOString(),
      requestedLogin: data.requestedLogin ? dayjs(data.requestedLogin).toISOString() : null,
      requestedLogout: data.requestedLogout ? dayjs(data.requestedLogout).toISOString() : null,
    };

    const res = await post('/users/correction', { ...payload });

    if (res.status == 200) {
      enqueueSnackbar(res.message || 'Success', { variant: 'success', autoHideDuration: 3000 });
    } else {
      enqueueSnackbar(res.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
    }
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Attendance Regularization</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} mt={1}>
          {/* Date */}
          <Grid item size={{ xs: 12 }}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Date"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: errors.date?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Login Time */}
          <Grid item size={{ xs: 6 }}>
            <Controller
              name="requestedLogin"
              control={control}
              render={({ field }) => (
                <TimePicker
                  label="Login Time"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Logout Time */}
          <Grid item size={{ xs: 6 }}>
            <Controller
              name="requestedLogout"
              control={control}
              render={({ field }) => (
                <TimePicker
                  label="Logout Time"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Reason */}
          <Grid item size={{ xs: 12 }}>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reason"
                  multiline
                  rows={3}
                  fullWidth
                  error={!!errors.reason}
                  helperText={errors.reason?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceRegularizationDialog;
